import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Calendar, Repeat, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { usePermission } from '../rbac/use-permission';
import { paymentsApi, type Forecast } from '../../lib/api/payments';
import { P } from '../../lib/rbac/permissions.generated';

interface Props {
  standortId?: number;
  daysAhead?: number;
  embedded?: boolean;
}

function formatEur(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function PaymentForecastContent({ standortId, daysAhead = 30 }: { standortId?: number; daysAhead?: number }) {
  const [data, setData] = useState<Forecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const prognoseErlaubt = usePermission(P.buchhaltung.finanzdaten_und_zahlungen.liquiditaetsprognose_ansehen);

  useEffect(() => {
    setLoading(true);
    setError(null);
    paymentsApi
      .forecast({ standortId, daysAhead })
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [standortId, daysAhead]);

  if (loading) return <p className="text-sm text-muted-foreground">Lade…</p>;
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!data) return null;

  const summary = data.summary ?? {
    initialCount: 0,
    initialVolumeCents: 0,
    installmentCount: 0,
    installmentVolumeCents: 0,
    followupCount: 0,
    followupVolumeCents: 0,
    totalCount: 0,
    totalVolumeCents: 0,
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-2xl font-bold tabular-nums">{formatEur(summary.totalVolumeCents)}</div>
        <div className="text-xs text-muted-foreground">erwartet in nächsten {daysAhead} Tagen</div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-md border p-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" /> Forderungen
          </div>
          <div className="mt-1 text-lg font-semibold tabular-nums">{formatEur(summary.initialVolumeCents)}</div>
          <div className="text-xs text-muted-foreground">{summary.initialCount} Posten</div>
        </div>
        <div className="rounded-md border p-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Repeat className="h-3 w-3" /> Raten
          </div>
          <div className="mt-1 text-lg font-semibold tabular-nums">{formatEur(summary.installmentVolumeCents)}</div>
          <div className="text-xs text-muted-foreground">{summary.installmentCount} fällige Rate{summary.installmentCount === 1 ? '' : 'n'}</div>
        </div>
        <div className="rounded-md border p-3" title="Geschätzt aus Stundenplan und Restkontingent — Absagen können das Datum verschieben">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <RefreshCw className="h-3 w-3" /> Folgepakete*
          </div>
          <div className="mt-1 text-lg font-semibold tabular-nums">{formatEur(summary.followupVolumeCents)}</div>
          <div className="text-xs text-muted-foreground">{summary.followupCount} Nachbuchung{summary.followupCount === 1 ? '' : 'en'}</div>
        </div>
      </div>

      {summary.totalCount === 0 && (
        <p className="text-xs text-muted-foreground">Keine geplanten Zahlungen in diesem Zeitraum.</p>
      )}

      {summary.followupCount > 0 && (
        <p className="text-[10px] text-muted-foreground">
          * Folgepakete sind geschätzt: Sie entstehen, sobald das Kontingent laut Stundenplan aufgebraucht ist. Absagen können das Datum verschieben.
        </p>
      )}

      {prognoseErlaubt && (
        <Link to="/dashboard/accounting/liquiditaet" className="inline-block text-xs text-primary hover:underline">
          Zur Liquiditätsprognose →
        </Link>
      )}
    </div>
  );
}

export default function PaymentForecastWidget({ standortId, daysAhead = 30, embedded = false }: Props) {
  const content = <PaymentForecastContent standortId={standortId} daysAhead={daysAhead} />;
  if (embedded) return content;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-4 w-4 text-primary" />
          Forecast (nächste {daysAhead} Tage)
        </CardTitle>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
